import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useSmartBack } from '../../hooks/useSmartBack';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  ArrowLeft, Loader2, DollarSign, Download, ChevronRight, Play, AlertTriangle
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Separator } from '../../components/ui/separator';
import { PayoutDecision } from '../../components/admin/PayoutDecision';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  pending: 'bg-amber-100 text-amber-700',
  approved: 'bg-blue-100 text-blue-700',
  paid: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
};

const AdminPayoutsPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { back } = useSmartBack('/admin/dashboard');
  const [payouts, setPayouts] = useState([]);
  const [deductions, setDeductions] = useState([]);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [triggering, setTriggering] = useState(false);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchData = useCallback(async () => {
    try {
      const params = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const [payRes, dedRes, runRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/payouts${params}`, { headers }),
        axios.get(`${API_URL}/api/admin/deductions`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/admin/payout-runs`, { headers }).catch(() => ({ data: [] })),
      ]);
      setPayouts(payRes.data);
      setDeductions(dedRes.data || []);
      setRuns(runRes.data || []);
    } catch { toast.error('Failed to load payouts'); }
    finally { setLoading(false); }
  }, [headers, statusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleTriggerWeekly = async () => {
    setTriggering(true);
    try {
      await axios.post(`${API_URL}/api/admin/payouts/trigger-weekly`, {}, { headers });
      toast.success('Weekly payout processing triggered');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to trigger payouts'); }
    finally { setTriggering(false); }
  };

  const handleExport = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/payouts/export`, { headers, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'payouts.csv'; a.click();
      toast.success('CSV downloaded');
    } catch { toast.error('Failed to export'); }
  };

  const [generating, setGenerating] = useState(false);
  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/payouts/generate`, {}, { headers });
      toast.success(res.data.message);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to generate payouts'); }
    finally { setGenerating(false); }
  };

  const totalPending = payouts.filter(p => ['pending', 'approved'].includes(p.status)).reduce((s, p) => s + p.net_payout, 0);
  const totalPaid = payouts.filter(p => p.status === 'paid').reduce((s, p) => s + p.net_payout, 0);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-zinc-50"><Loader2 className="h-8 w-8 animate-spin text-[#F56F05]" /></div>;

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="admin-payouts-page">
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex items-center min-h-16 gap-4 py-3 flex-wrap">
          <Button variant="ghost" size="icon" onClick={back} data-testid="back-btn"><ArrowLeft className="h-5 w-5" /></Button>
          <div className="flex-1 min-w-0"><h1 className="text-xl font-heading text-zinc-900">Payouts</h1><p className="text-sm text-zinc-500 truncate">{payouts.length} payouts, {deductions.length} deductions</p></div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={handleExport} data-testid="export-btn"><Download className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">Export</span></Button>
            <Button variant="outline" size="sm" onClick={handleGenerate} disabled={generating} data-testid="generate-payouts-btn">
              {generating ? <Loader2 className="h-4 w-4 animate-spin sm:mr-2" /> : <DollarSign className="h-4 w-4 sm:mr-2" />}<span className="hidden sm:inline">Generate</span>
            </Button>
            <Button size="sm" className="bg-[#F56F05] hover:bg-[#C2410C] text-white" onClick={handleTriggerWeekly} disabled={triggering} data-testid="trigger-payouts-btn">
              {triggering ? <Loader2 className="h-4 w-4 animate-spin sm:mr-2" /> : <Play className="h-4 w-4 sm:mr-2" />}<span className="hidden sm:inline">Run Weekly</span>
            </Button>
          </div>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-4">
        {/* Summary */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Card className="border-0 shadow-sm"><CardContent className="p-4">
            <p className="text-xs text-zinc-400 uppercase">Pending / Approved</p>
            <p className="text-xl font-bold text-amber-600">${totalPending.toFixed(2)}</p>
          </CardContent></Card>
          <Card className="border-0 shadow-sm"><CardContent className="p-4">
            <p className="text-xs text-zinc-400 uppercase">Total Paid</p>
            <p className="text-xl font-bold text-green-600">${totalPaid.toFixed(2)}</p>
          </CardContent></Card>
          <Card className="border-0 shadow-sm"><CardContent className="p-4">
            <p className="text-xs text-zinc-400 uppercase">Payout Runs</p>
            <p className="text-xl font-bold text-zinc-700">{runs.length}</p>
          </CardContent></Card>
        </div>

        {/* Filter */}
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[160px]" data-testid="status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
          </SelectContent>
        </Select>

        {/* Payouts List */}
        {payouts.length === 0 ? (
          <Card className="border-0 shadow-sm"><CardContent className="p-12 text-center"><DollarSign className="h-12 w-12 text-zinc-300 mx-auto mb-3" /><p className="text-zinc-500">No payouts yet</p><p className="text-zinc-400 text-sm mt-1">Payouts are generated when weekly processing runs.</p></CardContent></Card>
        ) : (
          <div className="space-y-2">
            {payouts.map((p, i) => (
              <motion.div key={p.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.02 }}>
                <Card className="border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={() => { setSelectedPayout(p); setDetailOpen(true); }} data-testid={`payout-${p.id}`}>
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
                      <DollarSign className="h-5 w-5 text-emerald-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-zinc-900">{p.cleaner_name || 'Unknown'}</p>
                      <p className="text-sm text-zinc-500">Week of {p.week_start ? new Date(p.week_start).toLocaleDateString() : 'N/A'}</p>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <p className="font-semibold text-zinc-900">${p.net_payout.toFixed(2)}</p>
                      <Badge variant="outline" className={`text-xs ${STATUS_COLORS[p.status] || 'bg-zinc-100 text-zinc-600'}`}>{p.status}</Badge>
                      <ChevronRight className="h-4 w-4 text-zinc-300" />
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}

        {/* Deductions */}
        {deductions.length > 0 && (
          <Card className="border-0 shadow-sm">
            <CardHeader className="border-b border-zinc-100">
              <CardTitle className="text-lg font-heading flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-orange-500" />Deductions</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y divide-zinc-100">
                {deductions.map(d => (
                  <div key={d.id} className="flex items-center justify-between p-4" data-testid={`deduction-${d.id}`}>
                    <div>
                      <p className="font-medium text-zinc-900">{d.cleaner_name || 'Unknown'}</p>
                      <p className="text-sm text-zinc-500 capitalize">{d.deduction_type} {d.note && `- ${d.note}`}</p>
                    </div>
                    <p className="font-semibold text-red-600">-${d.amount.toFixed(2)}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </main>

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Payout Details</DialogTitle>
            <DialogDescription>{selectedPayout?.cleaner_name || 'Cleaner'}</DialogDescription>
          </DialogHeader>
          {selectedPayout && (
            <div className="space-y-4 mt-2">
              <Badge variant="outline" className={STATUS_COLORS[selectedPayout.status]}>{selectedPayout.status}</Badge>
              <div className="bg-zinc-50 rounded-lg p-4 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-zinc-500">Labor</span><span className="font-medium">${(selectedPayout.labor_amount || 0).toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-zinc-500">Commission</span><span className="font-medium">${(selectedPayout.commission_amount || 0).toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-zinc-500">Tips</span><span className="font-medium text-green-600">+${(selectedPayout.tip_amount || 0).toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-zinc-500">Deductions</span><span className="font-medium text-red-600">-${(selectedPayout.deductions_amount || 0).toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-zinc-500">Adjustments</span><span className={`font-medium ${(selectedPayout.adjustments_amount || 0) > 0 ? 'text-red-600' : (selectedPayout.adjustments_amount || 0) < 0 ? 'text-green-600' : ''}`} data-testid="payout-adjustments-amount">{(selectedPayout.adjustments_amount || 0) > 0 ? '-' : (selectedPayout.adjustments_amount || 0) < 0 ? '+' : ''}${Math.abs(selectedPayout.adjustments_amount || 0).toFixed(2)}</span></div>
                <Separator />
                <div className="flex justify-between"><span className="font-medium text-zinc-900">Net Payout</span><span className="font-bold text-lg">${selectedPayout.net_payout.toFixed(2)}</span></div>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-zinc-400">Pay Method</span><p className="font-medium capitalize">{selectedPayout.pay_method}</p></div>
                <div><span className="text-zinc-400">Week</span><p className="font-medium">{selectedPayout.week_start ? new Date(selectedPayout.week_start).toLocaleDateString() : 'N/A'}</p></div>
                {selectedPayout.paid_at && <div><span className="text-zinc-400">Paid At</span><p className="font-medium">{new Date(selectedPayout.paid_at).toLocaleString()}</p></div>}
                {selectedPayout.stripe_transfer_id && <div><span className="text-zinc-400">Stripe Transfer</span><p className="font-medium text-xs break-all">{selectedPayout.stripe_transfer_id}</p></div>}
              </div>
              <PayoutDecision payout={selectedPayout} headers={headers} onSaved={async () => { await fetchData(); const r = await axios.get(`${API_URL}/api/admin/payouts`, { headers }); setSelectedPayout(r.data.find((x) => x.id === selectedPayout.id) || null); }} />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPayoutsPage;
